import { CompanyContact } from './company-contact';

/**
 * Sorts company contacts in ascending order by display order, with nulls at the end.
 * Then sorts by role_desc, party_organization_display_name, and location_desc if available.
 * @param companyContacts - company contacts to be sorted
 * @returns sorted company contacts
 */
export const SortByDisplayOrder = (companyContacts: CompanyContact[]): CompanyContact[] => {
  return companyContacts.sort(
    (a, b) =>
      compareDisplayOrder(a, b) ||
      compareRoleDesc(a, b) ||
      comparePartyOrganizationDisplayName(a, b) ||
      compareLocationName(a, b)
  );
};

const compareDisplayOrder = (a: CompanyContact, b: CompanyContact): number => {
  if (a.display_order == null && b.display_order == null) {
    return 0; // Both are null, so they are considered equal
  }
  if (a.display_order == null) return 1;
  if (b.display_order == null) return -1;
  return a.display_order - b.display_order;
};

// sort asc by role_desc but send nulls to the end
const compareRoleDesc = (a: CompanyContact, b: CompanyContact): number => {
  if (a.role_desc == null && b.role_desc == null) {
    return 0; // Both are null, so they are considered equal
  }
  if (a.role_desc == null) return 1;
  if (b.role_desc == null) return -1;

  return a.role_desc.localeCompare(b.role_desc);
};

const comparePartyOrganizationDisplayName = (a: CompanyContact, b: CompanyContact): number => {
  if (a.party_organization_display_name == null && b.party_organization_display_name == null) {
    return 0; // Both are null, so they are considered equal
  }
  if (a.party_organization_display_name == null) return 1;
  if (b.party_organization_display_name == null) return -1;
  return a.party_organization_display_name.localeCompare(b.party_organization_display_name);
};

const compareLocationName = (a: CompanyContact, b: CompanyContact): number => {
  if (a.location?.location_desc == null && b.location?.location_desc == null) {
    return 0; // Both are null, so they are considered equal
  }
  if (a.location?.location_desc == null) return 1;
  if (b.location?.location_desc == null) return -1;

  const aLocationName = a.location?.location_desc;
  const bLocationName = b.location?.location_desc;
  return aLocationName.localeCompare(bLocationName);
};

/**
 * Sorts contacts in ascending order by display order, with nulls at the end.
 * Then sorts by contact name (use familiar_name, if empty fallback contact_display_name)
 * @param companyContacts - company contacts to be sorted
 * @returns sorted company contacts
 */
export const SortContactsByDisplayOrder = (companyContacts: CompanyContact[]): CompanyContact[] => {
  return companyContacts.sort((a, b) => compareDisplayOrder(a, b) || compareContactName(a, b));
};

function compareContactName(a: CompanyContact, b: CompanyContact): number {
  const aName = a.familiar_name || a.contact_display_name;
  const bName = b.familiar_name || b.contact_display_name;

  if (aName == null && bName == null) {
    return 0;
  }

  if (aName == null) return 1;
  if (bName == null) return -1;

  return aName.localeCompare(bName);
}
